import {
  Alert,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import KeyIcon from '@mui/icons-material/Key'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import React, { useEffect, useState } from 'react'
import {
  browserSupportsWebAuthn,
  startRegistration,
} from '@simplewebauthn/browser'
import API from '../../../services/API'
import BigModal from '../../../components/Modals/BigModal'

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString() : null

const PasskeySettings = ({ onRequireMFA }) => {
  const [passkeys, setPasskeys] = useState([])
  const [addOpen, setAddOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const supported = browserSupportsWebAuthn()

  const loadPasskeys = () => {
    API.get('/auth/passkey/list').then((res) => {
      if (res.status === 200) {
        setPasskeys(res.data.passkeys || [])
      }
    })
  }

  useEffect(loadPasskeys, [])

  const handleAdd = async () => {
    setBusy(true)
    setError('')
    const optionsRes = await API.post('/auth/passkey/registration-options')
    if (optionsRes.status === 403) {
      setBusy(false)
      setAddOpen(false)
      onRequireMFA(() => setAddOpen(true))
      return
    }
    if (optionsRes.status === 406) {
      setBusy(false)
      setError('You have reached the maximum number of passkeys.')
      return
    }
    if (optionsRes.status !== 200) {
      setBusy(false)
      setError('Could not start passkey registration. Please try again.')
      return
    }
    try {
      const regResponse = await startRegistration({
        optionsJSON: optionsRes.data,
      })
      const registerRes = await API.post('/auth/passkey/register', {
        label,
        response: regResponse,
      })
      if (registerRes.status === 200) {
        setPasskeys(registerRes.data.passkeys || [])
        setAddOpen(false)
        setLabel('')
      } else {
        setError('Passkey registration failed. Please try again.')
      }
    } catch (e) {
      // NotAllowedError is the user dismissing the browser prompt.
      if (e?.name !== 'NotAllowedError') {
        setError('Passkey registration failed. Please try again.')
      }
    }
    setBusy(false)
  }

  const handleDelete = async (passkeyID) => {
    const res = await API.delete(`/auth/passkey/${passkeyID}`)
    if (res.status === 403) {
      onRequireMFA(() => handleDelete(passkeyID))
      return
    }
    if (res.status === 200) {
      setPasskeys(res.data.passkeys || [])
    }
  }

  if (!supported) return null

  return (
    <Stack gap={2} component={Paper} padding={1}>
      <Typography variant="title2">Passkeys</Typography>
      <Alert severity="info" icon={<KeyIcon fontSize="inherit" />}>
        Passkeys let you verify with Touch ID, Face ID, Windows Hello or a
        security key instead of typing an authenticator code each time.
      </Alert>
      {passkeys.length > 0 && (
        <List dense disablePadding>
          {passkeys.map((passkey) => (
            <ListItem
              key={passkey._id}
              secondaryAction={
                <Tooltip title="Remove passkey">
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleDelete(passkey._id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <KeyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={passkey.label || 'Passkey'}
                secondary={[
                  passkey.createdAt && `Added ${formatDate(passkey.createdAt)}`,
                  passkey.lastUsedAt &&
                    `Last used ${formatDate(passkey.lastUsedAt)}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </ListItem>
          ))}
        </List>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => {
          setError('')
          setAddOpen(true)
        }}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add Passkey
      </Button>
      <BigModal autoWidth open={addOpen} onClose={() => setAddOpen(false)}>
        <Stack padding={1} gap={2} minWidth={{ sm: 340 }}>
          <Typography variant="title3">Add a Passkey</Typography>
          <Typography variant="body2">
            Give this passkey a name so you can recognise it later, then follow
            your browser's prompts.
          </Typography>
          <TextField
            label="Passkey name"
            size="small"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Work laptop"
            inputProps={{ maxLength: 60 }}
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={busy || !label.trim()}
          >
            {busy ? 'Waiting for browser...' : 'Create Passkey'}
          </Button>
        </Stack>
      </BigModal>
    </Stack>
  )
}

export default PasskeySettings
